import {
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
} from '@mui/material'
import { GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import React, { useCallback, useEffect, useState } from 'react'
import { DataTable } from '@components/tables'
import { useSelector } from '@state/store'

import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import { useAppDispatch } from '@hooks/reduxHooks'
import {
  deleteAmbulanceCompany,
  findAmbulanceCompanies,
} from '@state/thunks/ambulanceThunk'
import { AmbulanceForm } from './AmbulanceForm'
import { EditIcon } from 'lucide-react'
import { AmbulanceCompanyDto } from '@services/api'

const AmbulanceCompanies: React.FC = () => {
  const dispatch = useAppDispatch()
  const ambulances = useSelector((state) => state.ambulance.data)
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [editingAmbulance, setEditingAmbulance] = useState<
    AmbulanceCompanyDto | undefined
  >(undefined)

  useEffect(() => {
    setIsLoading(false)
    dispatch(findAmbulanceCompanies())
      .unwrap()
      .then(() => {
        setIsLoading(false)
      })
  }, [])

  const columns: GridColumns = [
    {
      field: 'name',
      headerName: 'Nom',
      flex: 1,
    },
    {
      field: 'address',
      headerName: 'Adresse',
      flex: 1,
    },
    {
      field: 'city',
      headerName: 'Ville',
      flex: 1,
    },
    {
      field: 'zipCode',
      headerName: 'Code postal',
      flex: 1,
    },
    {
      field: 'phone1',
      headerName: 'Téléphone 1',
      flex: 1,
    },
    {
      field: 'phone2',
      headerName: 'Téléphone 2',
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'Email de contact',
      flex: 1,
    },
    { field: 'siret', headerName: 'Siret', flex: 1 },
    // {
    //   field: 'sites',
    //   headerName: 'Sites',
    //   flex: 1,
    //   renderCell: ({ row }) =>
    //     row.sites.map((s) => <span key={s.id}>{s.label}</span>),
    // },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      flex: 1,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => {
            setEditingAmbulance(params.row)
            setOpenAddDialog(true)
          }}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ]

  const handleCloseAddDialog = useCallback(() => {
    setOpenAddDialog(false)
    setEditingAmbulance(undefined)
  }, [])

  const onRefresh = useCallback(() => {
    dispatch(findAmbulanceCompanies())
  }, [dispatch])

  useEffect(() => {
    onRefresh()
  }, [dispatch, onRefresh])

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection as number[])
  }

  const _onDeleteConfirm = () => {
    dispatch(deleteAmbulanceCompany(selectedRows))
    setIsDeleteDialogOpen(false)
  }

  return (
    <Container maxWidth={false} style={{ height: '89vh', padding: '0 5px' }}>
      <Paper
        elevation={2}
        style={{ width: '100%', borderRadius: '0 15px 15px 15px' }}
      >
        <DataTable
          columns={columns}
          rows={ambulances}
          detailPanelHeight={600}
          loading={isLoading}
          getRowId={(row) => row.id}
          toolBarProps={{
            title: 'ambulance',
            icon: 'fas fa-ambulance',
            onAdd: () => {
              setEditingAmbulance(undefined)
              setOpenAddDialog(true)
            },
            onRefresh,
            onDelete: () => {
              setIsDeleteDialogOpen(true)
            },
          }}
          checkboxSelection
          onSelectionModelChange={_onSelectionChange}
        />
      </Paper>
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="lg"
        aria-labelledby="form-dialog-title"
        scroll="paper"
      >
        <DialogTitle>
          {editingAmbulance ? 'Modifier' : 'Ajouter'} une ambulance
        </DialogTitle>
        <DialogContent>
          <AmbulanceForm
            onClose={handleCloseAddDialog}
            row={editingAmbulance}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description="Êtes-vous sûr de vouloir supprimer ces ambulances sélectionnées ?"
      />
    </Container>
  )
}

export default AmbulanceCompanies
